import React, { useMemo } from 'react';
import styled from 'styled-components';
import { vars } from '../../theme';
import type { TExtendedSize } from '../../tokens/types';
import { guid } from '../../utils/shared.utils';

type TLogoType = 'wordmark' | 'monogram';
type TLogoSize = TExtendedSize;

export interface Props {
    /** CSS class applied to the root element. */
    className?: string;
    /** Selects between the full "HoneyBook" wordmark or the standalone monogram icon. */
    type?: TLogoType;
    /** Controls the rendered dimensions. 'xs' is the smallest; 'xl' (default) is the largest. */
    size?: TLogoSize;
    /** Fill color applied to the logo SVG via a mask technique. */
    color?: string;
    /** String appended to the logo SVG filename before the extension (e.g. `_dark` loads `logo_dark.svg`). */
    suffix?: string;
}

export const SIZES = {
    wordmark: {
        xs: {
            height: 8,
            width: 71
        },
        sm: {
            height: 11,
            width: 97
        },
        md: {
            height: 14,
            width: 124
        },
        lg: {
            height: 18,
            width: 159
        },
        xl: {
            height: 24,
            width: 205
        }
    },
    monogram: {
        xs: {
            height: 16,
            width: 16
        },
        sm: {
            height: 22,
            width: 22
        },
        md: {
            height: 28,
            width: 28
        },
        lg: {
            height: 38,
            width: 38
        },
        xl: {
            height: 50,
            width: 50
        }
    }
};

export const BASE_URL =
    'https://hbstatic.honeybook.com/assets_system/system_web/logos';

export const getLogoUrl = (type: TLogoType, suffix?: string): string => {
    return `${BASE_URL}/logo${type === 'monogram' ? '_monogram' : ''}${
        suffix ? suffix : ''
    }.svg`;
};

const HBLogo = ({
    className,
    type = 'wordmark',
    size = 'xl',
    color = vars.colors.text.primary,
    suffix
}: Props) => {
    const maskId = useMemo(() => guid(), []);

    const sizes = SIZES[type][size];

    return (
        <LogoSVG
            {...sizes}
            className={className}
            xmlns="http://www.w3.org/2000/svg"
            viewBox={`0 0 ${sizes.width} ${sizes.height}`}
            role="img"
            aria-label="HoneyBook logo"
        >
            {/* Using an SVG mask to apply the logo shape to a colored rectangle below it */}
            <LogoMask id={`fillMaskComponent-${maskId}`} {...sizes}>
                <LogoImage href={getLogoUrl(type, suffix)} {...sizes} />
            </LogoMask>
            <LogoRect
                fill={color}
                mask={`url("#fillMaskComponent-${maskId}")`}
                {...sizes}
            />
        </LogoSVG>
    );
};

export default HBLogo;

const LogoSVG = styled.svg`
    display: block;
    vertical-align: top;
`;

const LogoMask = styled.mask<{ width: number; height: number }>`
    height: ${props => props.height}px;
    width: ${props => props.width}px;
`;

const LogoImage = styled.image<{ width: number; height: number }>`
    height: ${props => props.height}px;
    width: ${props => props.width}px;
`;

const LogoRect = styled.rect<{ width: number; height: number }>`
    height: ${props => props.height}px;
    width: ${props => props.width}px;
`;
